import { MemoizedSelector, MemoizedSelectorWithProps } from '@ngrx/store';

import { createSelectorWithProps, createSimpleSelector, namedProjector } from '../selector-utils';
import { MetaState, Reference } from '../types';
import { createSelectorWithReferences } from '../utils';
import { BaseEntity, BaseSelector, EntityState, ID, MetaScopeOptions, Scope } from './types';
import { resolveScope } from './utils';

type MetaSelectorProps = ID | { scope: Scope };

type SelectorOptionalProps<T extends BaseEntity, Props, Result> = MemoizedSelector<
  EntityState<T>,
  Result
> &
  MemoizedSelectorWithProps<EntityState<T>, Props | void, Result>;

const getMetaSelector = <T extends BaseEntity, K extends keyof MetaState>(
  metaProp: K,
  baseSelector: BaseSelector<T>,
  namespace: string,
): SelectorOptionalProps<T, MetaSelectorProps, MetaState[K]> =>
  createSelectorWithProps(
    baseSelector,
    namedProjector(
      `${namespace}${metaProp}`,
      (state: EntityState<T>, scopeOrEntityId?: MetaSelectorProps) => {
        if (!scopeOrEntityId || (<{ scope: Scope }>scopeOrEntityId)?.scope) {
          const scope = resolveScope((<{ scope: Scope }>scopeOrEntityId)?.scope);
          return state.scopeMeta[scope]?.[metaProp];
        } else {
          return state.entityMetaMap[<ID>scopeOrEntityId]?.[metaProp];
        }
      },
    ),
  );

export const getSelectors = <T extends BaseEntity>(
  baseSelector: BaseSelector<T>,
  namespace: string,
  references: Reference<T>[] | null,
) => {
  const entityMap = createSimpleSelector(baseSelector, state => state.entityMap);
  const aliases = createSimpleSelector(baseSelector, state => state.aliases);
  const byId = createSelectorWithProps(
    entityMap,
    aliases,
    namedProjector(
      `${namespace}ById`,
      (
        entities: EntityState<T>['entityMap'],
        aliases: EntityState<T>['aliases'],
        id: string | number,
      ) => {
        const entityId = aliases[id] || id;
        return entities[entityId] || null;
      },
    ),
  );

  const entities = <
    MemoizedSelector<any, T[]> & MemoizedSelectorWithProps<any, MetaScopeOptions, T[]>
  >createSelectorWithProps(
    baseSelector,
    namedProjector(`${namespace}Entities`, (state: EntityState<T>, props?: MetaScopeOptions) => {
      const scope = resolveScope(props?.scope);
      return (state && state.scope[scope]?.map(id => state.entityMap[id]!)) || [];
    }),
  );

  const pagination = createSelectorWithProps(
    baseSelector,
    namedProjector(`${namespace}Pagination`, (state: EntityState<T>, props?: { scope?: Scope }) => {
      const scope = resolveScope(props?.scope);
      return state.scopePagination[scope];
    }),
  );

  return {
    isLoaded: getMetaSelector<T, 'loaded'>('loaded', baseSelector, namespace),
    isPending: getMetaSelector<T, 'pending'>('pending', baseSelector, namespace),
    isFailed: getMetaSelector<T, 'failed'>('failed', baseSelector, namespace),
    error: getMetaSelector<T, 'error'>('error', baseSelector, namespace),
    pagination,
    byId,
    byIdWithReferences: createSelectorWithReferences(
      byId,
      references,
      namedProjector(`${namespace}ByIdWithReferences`, state => state),
    ),
    entities,
    entitiesWithPagination: createSelectorWithProps(
      pagination,
      entities,
      (pagination, entities) => ({
        meta: pagination,
        rows: entities,
      }),
    ),
    entitiesWithReferences: createSelectorWithReferences(
      entities,
      references,
      namedProjector(`${namespace}EntitiesWithReferences`, entities => entities),
    ),
  };
};
